/**
 * Timeline: board 05, section 5.3.
 *
 * Several terms plotted across the whole archive, with the two things that
 * make the chart honest printed beside it rather than hidden in a help page:
 * what the shading means, and the caveat that applies to every value drawn
 * from a scan.
 *
 * The terms live in the URL, so a chart is shareable the same way a search is.
 */

import { useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  api,
  type EditionRow,
  type Facets,
  type LandingFacts,
  type Timeline as TimelineData,
  type TimelinePoint,
} from '../lib/api'
import { PanelLabel, Segmented } from '../components/Page'
import {
  MAX_TERMS,
  SERIES_COLOURS,
  Timeline,
  rateOf,
  type TimelineMeasure,
  type TimelineSeries,
} from '../components/Timeline'
import { n } from '../components/search/model'

export function TimelinePage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const terms = useMemo(() => {
    const raw = params.get('terms') ?? params.get('q') ?? ''
    return raw
      .split(',')
      .map((term) => term.trim())
      .filter(Boolean)
      .slice(0, MAX_TERMS)
  }, [params])
  const measure: TimelineMeasure = params.get('rate') === 'count' ? 'count' : 'rate'

  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState(false)
  const [saved, setSaved] = useState(false)

  function setTerms(next: string[]) {
    const merged = new URLSearchParams(params)
    if (next.length > 0) merged.set('terms', next.join(','))
    else merged.delete('terms')
    merged.delete('q')
    setParams(merged)
  }

  function setMeasure(next: TimelineMeasure) {
    const merged = new URLSearchParams(params)
    if (next === 'count') merged.set('rate', 'count')
    else merged.delete('rate')
    setParams(merged)
  }

  /*
   * The contract takes several comma-separated terms on one request. While
   * that is being built, one request per term returns the same measurement, so
   * the chart is drawn either way and neither path invents a point.
   */
  const combined = useQuery({
    queryKey: ['timeline-combined', terms.join(','), measure],
    queryFn: () =>
      api.get<TimelineData>(
        `/search/timeline?${new URLSearchParams({
          q: terms[0] ?? '',
          terms: terms.join(','),
          rate: measure === 'rate' ? 'true' : 'false',
        }).toString()}`,
      ),
    enabled: terms.length > 0,
  })

  const perTerm = useQueries({
    queries: terms.map((term) => ({
      queryKey: ['timeline-term', term],
      queryFn: () => api.get<TimelineData>(`/search/timeline?q=${encodeURIComponent(term)}`),
      enabled: terms.length > 0 && !combined.data?.series?.length,
    })),
  })

  const series: TimelineSeries[] = useMemo(() => {
    if (combined.data?.series?.length) {
      return combined.data.series
        .map((line) => ({ term: line.term, points: line.points }))
        .slice(0, MAX_TERMS)
    }
    return terms
      .map((term, index) => ({ term, points: perTerm[index]?.data?.points ?? [] }))
      .filter((line) => line.points.length > 0)
  }, [combined.data, perTerm, terms])

  const facts = useQuery({
    queryKey: ['landing'],
    queryFn: () => api.get<LandingFacts>('/landing'),
    staleTime: 5 * 60 * 1000,
  })

  const editions = useQuery({
    queryKey: ['editions'],
    queryFn: () => api.get<EditionRow[]>('/editions'),
    staleTime: 5 * 60 * 1000,
  })

  const digitalFrom = useMemo(() => firstDigitalYear(editions.data), [editions.data])

  // Which years the archive holds an edition for at all, counted over the
  // whole corpus rather than over this query. A year with no edition is a hole
  // in the evidence, and the chart bands it rather than drawing a line across
  // it as though the value were zero.
  const holdings = useQuery({
    queryKey: ['timeline-holdings'],
    queryFn: () => api.get<Facets>('/search/facets'),
    staleTime: 5 * 60 * 1000,
  })
  const missingYears = useMemo(
    () => missingFrom(holdings.data, series),
    [holdings.data, series],
  )

  // Which plotted years the server marked as a floor, and whether they form
  // one leading era with a boundary the caveat can name.
  const floorYears = useMemo(() => floorYearsOf(series), [series])
  const floorBoundary = useMemo(() => {
    if (floorYears.length === 0) return null
    const plotted = series
      .flatMap((line) => line.points.map((point) => Number(point.period.slice(0, 4))))
      .filter((year) => Number.isFinite(year))
      .sort((a, b) => a - b)
    const contiguous = floorYears.every((year, index) => year === plotted[index])
    return contiguous && floorYears.length < plotted.length
      ? floorYears[floorYears.length - 1] + 1
      : null
  }, [floorYears, series])

  // The caveat's figure, counted: how much of the scanned era the recognition
  // itself doubts. Asked for exactly the window the caveat covers.
  const scannedConfidence = useQuery({
    queryKey: ['timeline-caveat', digitalFrom, terms.join(',')],
    queryFn: () =>
      api.get<Facets>(
        `/search/facets?${new URLSearchParams({
          q: terms.join(' '),
          date_to: `${(digitalFrom as number) - 1}-12-31`,
        }).toString()}`,
      ),
    enabled: digitalFrom !== null && terms.length > 0,
  })

  const loading = combined.isFetching || perTerm.some((query) => query.isFetching)
  const span = spanOf(series)

  function addTerm(term: string) {
    const trimmed = term.trim()
    if (!trimmed || terms.includes(trimmed) || terms.length >= MAX_TERMS) return
    setTerms([...terms, trimmed])
    setDraft('')
    setAdding(false)
  }

  async function saveAsSearch() {
    if (terms.length === 0) return
    const name = window.prompt('Name this search', terms.join(' OR '))
    if (!name) return
    await api.post('/saved-searches', { name, query: { q: terms.join(' ') } })
    setSaved(true)
  }

  function downloadCsv() {
    const blob = new Blob([toCsv(series, measure)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `term-frequency-${terms.join('-') || 'empty'}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto w-full max-w-[1680px] p-4 md:p-6">
      <div className="overflow-hidden rounded-[--radius-md] border border-rule2 bg-paper shadow-md">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-n300 px-[22px] pb-3.5 pt-4">
          <div>
            <div className="mb-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink2">
              {measure === 'rate' ? 'Mentions per 10,000 units' : 'Matching units'}
              {facts.data?.publication ? ` · ${facts.data.publication}` : ''}
              {span ? ` · ${span}` : ''}
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {terms.map((term, index) => (
                <div
                  key={term}
                  className="flex items-center gap-[7px] rounded-[--radius-md] border px-[11px] py-[5px] text-[14px]"
                  style={{ borderColor: SERIES_COLOURS[index], color: SERIES_COLOURS[index] }}
                >
                  <span
                    className="h-[9px] w-[9px] flex-none"
                    style={{ background: SERIES_COLOURS[index] }}
                    aria-hidden="true"
                  />
                  {term}
                  <button
                    type="button"
                    onClick={() => setTerms(terms.filter((entry) => entry !== term))}
                    className="bg-transparent p-0 font-mono text-[11px] leading-none"
                    style={{ color: SERIES_COLOURS[index] }}
                    aria-label={`Remove ${term}`}
                  >
                    ×
                  </button>
                </div>
              ))}

              {adding || terms.length === 0 ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault()
                    addTerm(draft)
                  }}
                >
                  <input
                    autoFocus
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => terms.length > 0 && setAdding(false)}
                    placeholder="a term to plot"
                    aria-label="A term to plot"
                    className="rounded-[--radius-md] border border-rule2 bg-paper px-[11px] py-[5px] text-[14px] text-ink outline-none focus:border-accent"
                  />
                </form>
              ) : (
                terms.length < MAX_TERMS && (
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="rounded-[--radius-md] border border-rule2 px-[11px] py-[5px] text-[14px] text-ink2"
                  >
                    + add a term
                  </button>
                )
              )}
              {terms.length >= MAX_TERMS && (
                <span className="font-mono text-[10px] uppercase text-ink2">
                  four terms is the limit
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              size="sm"
              ariaLabel="Measure"
              value={measure}
              onChange={setMeasure}
              options={[
                { value: 'rate', label: 'Rate', title: 'Mentions per 10,000 searchable units' },
                { value: 'count', label: 'Count', title: 'Matching units, unadjusted' },
              ]}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={downloadCsv}
              disabled={series.length === 0}
            >
              Download CSV
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void saveAsSearch()}
              disabled={terms.length === 0 || saved}
            >
              {saved ? 'Saved' : 'Save as search'}
            </button>
          </div>
        </div>

        <div className="px-[22px] pb-4 pt-[22px]">
          {series.length > 0 ? (
            <Timeline
              series={series}
              measure={measure}
              digitalFrom={digitalFrom}
              missingYears={missingYears}
              onPickYear={(year) =>
                navigate(
                  `/search?${new URLSearchParams({
                    q: terms.join(' '),
                    date_from: `${year}-01-01`,
                    date_to: `${year}-12-31`,
                  }).toString()}`,
                )
              }
            />
          ) : (
            <p className="py-10 text-center text-[15px] text-ink2">
              {loading
                ? 'Counting…'
                : terms.length === 0
                  ? 'Add a term to plot how often the paper printed it.'
                  : 'Nothing matched those terms, so there is nothing to plot.'}
            </p>
          )}
        </div>

        <div className="grid gap-[26px] px-[22px] pb-[18px] md:grid-cols-[1.3fr_1fr_1fr]">
          <p className="m-0 text-[15.5px] leading-[1.5] text-ink2">
            Rate, not count, is the default: the paper printed a different number of pages in every
            decade, so raw counts would describe the newspaper&rsquo;s size rather than its
            attention. Clicking any year opens that year&rsquo;s results with the terms and date
            range already applied.
          </p>

          <div>
            <PanelLabel>What the shading means</PanelLabel>
            <div className="flex flex-col gap-[7px] text-[14px] leading-[1.4]">
              <ShadingRow
                swatch={
                  <span className="flex h-[11px] w-[22px] items-center justify-center">
                    <span className="block h-[2px] w-[22px] bg-accent" />
                    <span className="absolute h-[7px] w-[7px] rounded-full bg-accent" />
                  </span>
                }
              >
                Digital editions. Recognition is near-exact.
              </ShadingRow>
              <ShadingRow
                swatch={
                  <span
                    className="flex h-[11px] w-[22px] items-center justify-center"
                    style={{ background: 'var(--color-neutral-200)' }}
                  >
                    <span
                      className="block h-[7px] w-[7px] rounded-full bg-accent"
                      style={{
                        boxShadow:
                          '0 0 0 1.5px var(--color-bg), 0 0 0 3px var(--color-accent-2-700)',
                      }}
                    />
                  </span>
                }
              >
                Scanned editions, ringed. The count is a floor, not a fact.
              </ShadingRow>
              <ShadingRow
                swatch={
                  <span
                    className="block h-[11px] w-[22px]"
                    style={{
                      background:
                        'repeating-linear-gradient(135deg,var(--color-accent-2-100) 0 4px,transparent 4px 8px)',
                      border: '1px solid var(--color-accent-2-700)',
                    }}
                  />
                }
              >
                Editions missing from the archive.
              </ShadingRow>
            </div>
          </div>

          <div>
            <PanelLabel>Caveat shown with every chart</PanelLabel>
            {/*
              The caveat reads off the data rather than being printed at all
              times regardless: which years the server flagged as a floor is
              what decides whether there is a floor to warn about, and a chart
              built entirely from digital editions is told to say so rather
              than to apologise for a scan it does not contain.
            */}
            <p
              className={`m-0 px-3 py-2.5 text-[14px] leading-[1.45] text-ink ${
                floorYears.length === 0 ? 'bg-surface' : ''
              }`}
              style={
                floorYears.length === 0
                  ? { boxShadow: 'inset 2px 0 0 var(--color-neutral-500)' }
                  : {
                      background: 'var(--color-accent-2-100)',
                      boxShadow: 'inset 2px 0 0 var(--color-accent-2-700)',
                    }
              }
            >
              {floorYears.length === 0 ? (
                <>
                  Every value on this chart comes from digital editions, where the text was read
                  rather than recognised. Nothing here rests on a scan.
                </>
              ) : floorBoundary ? (
                <>
                  Before {floorBoundary} this line is built from recognised text.
                  {doubtSentence(scannedConfidence.data, floorBoundary)} A term the recognition
                  mangled is a term this chart cannot see. Treat pre-{floorBoundary} values as lower
                  bounds.
                </>
              ) : (
                <>
                  {floorYears.length === 1
                    ? `${floorYears[0]} is`
                    : `${n(floorYears.length)} of the years plotted are`}{' '}
                  built from recognised text. A term the recognition mangled is a term this chart
                  cannot see, so treat{' '}
                  {floorYears.length === 1 ? 'that value' : 'those values'} as lower bounds.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function ShadingRow({ swatch, children }: { swatch: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex gap-[9px]">
      <span className="relative mt-[3px] flex-none" aria-hidden="true">
        {swatch}
      </span>
      <span className="text-ink">{children}</span>
    </div>
  )
}

/**
 * The counted half of the caveat. It is added only when the count exists: a
 * caveat that invents its own figure is worse than a caveat without one.
 */
function doubtSentence(facets: Facets | undefined, digitalFrom: number): string {
  if (!facets) return ''
  const total = facets.total
  if (!total) return ''
  const low = facets.confidence_bands
    .filter((band) => band.value !== '0.90 and above')
    .reduce((sum, band) => sum + band.count, 0)
  if (!low) return ''
  return ` ${n(low)} of the ${n(total)} matches before ${digitalFrom} were recognised below 90% confidence.`
}

/** The plotted years the server flagged as a floor, in order. */
function floorYearsOf(series: TimelineSeries[]): number[] {
  const flagged = new Set<number>()
  for (const line of series) {
    for (const point of line.points) {
      if (point.floor) {
        const year = Number(point.period.slice(0, 4))
        if (Number.isFinite(year)) flagged.add(year)
      }
    }
  }
  return [...flagged].sort((a, b) => a - b)
}

/** The first year read by the digital pipeline, counted from the editions held. */
function firstDigitalYear(editions: EditionRow[] | undefined): number | null {
  if (!editions) return null
  const digital = editions
    .filter((edition) => edition.route === 'A' && edition.pub_date)
    .map((edition) => Number((edition.pub_date as string).slice(0, 4)))
    .filter((year) => Number.isFinite(year))
  const scanned = editions.filter((edition) => edition.route === 'B' && edition.pub_date)
  if (digital.length === 0 || scanned.length === 0) return null
  return Math.min(...digital)
}

/**
 * Years inside the plotted span that the archive holds no edition for. Only
 * years the facets actually enumerate count as held: a year the corpus never
 * mentions is a year with nothing in it, which is exactly the fact the band
 * is drawn to show.
 */
function missingFrom(holdings: Facets | undefined, series: TimelineSeries[]): number[] {
  if (!holdings?.years?.length) return []
  const held = new Set(
    holdings.years.filter((entry) => entry.editions > 0).map((entry) => entry.year),
  )
  const plotted = series
    .flatMap((line) => line.points.map((point) => Number(point.period.slice(0, 4))))
    .filter((year) => Number.isFinite(year))
  if (plotted.length === 0) return []
  const first = Math.min(...plotted)
  const last = Math.max(...plotted)
  if (last - first > 400) return []
  const out: number[] = []
  for (let year = first; year <= last; year += 1) {
    if (!held.has(year)) out.push(year)
  }
  return out
}

function spanOf(series: TimelineSeries[]): string | null {
  const years = series
    .flatMap((line) => line.points.map((point) => Number(point.period.slice(0, 4))))
    .filter((year) => Number.isFinite(year))
  if (years.length === 0) return null
  const first = Math.min(...years)
  const last = Math.max(...years)
  return first === last ? String(first) : `${first}–${last}`
}

/** The chart's own numbers, exactly as drawn, so a reader can check the picture. */
function toCsv(series: TimelineSeries[], measure: TimelineMeasure): string {
  const rows: string[] = ['term,period,matches,total,rate_per_10000,measure_plotted']
  for (const line of series) {
    for (const point of line.points) {
      rows.push(
        [
          quote(line.term),
          point.period,
          point.matches,
          point.total,
          rateOf(point).toFixed(4),
          measure,
        ].join(','),
      )
    }
  }
  return `${rows.join('\n')}\n`
}

function quote(value: string): string {
  return `"${value.replace(/"/g, '""')}"`
}

export type { TimelinePoint }
